import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TenantExpanded } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { Slider } from '#components/Slider.js'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { TenantDetails } from './TenantDetails'
import { TenantsList } from './TenantsList'

const TenantsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTenant, setSelectedTenant] = useState<TenantExpanded | null>(
    null,
  )
  const [search, setSearch] = useState(searchParams.get('search') || '')

  const {
    items: tenants,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<TenantExpanded>('tenants', {
    search,
    limit: 50,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    setSearchParams(params, { replace: true })
  }, [search, setSearchParams])

  const handleTenantSelect = useCallback((tenant: TenantExpanded) => {
    setSelectedTenant(tenant)
  }, [])

  return (
    <>
      <AdminListLayout
        title="Tenants"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No tenants found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <FilterInput
                label="Search tenants"
                value={search}
                onChange={setSearch}
                placeholder="Search..."
              />
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          tenants.length > 0 ? (
            <>
              <TenantsList
                tenants={tenants}
                selectedId={selectedTenant?._id}
                onSelect={handleTenantSelect}
              />
              {/* Sentinel element for infinite scroll */}
              <div ref={sentinelRef} className="h-4" />
              {/* Loading indicator */}
              {isFetchingNextPage ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md" />
                </div>
              ) : null}
            </>
          ) : null
        }
      />
      <Slider
        isOpen={!!selectedTenant}
        width="min(600px, 100%)"
        onClose={() => setSelectedTenant(null)}
        title={selectedTenant ? selectedTenant.username.toUpperCase() : ''}
      >
        <TenantDetails tenant={selectedTenant} />
      </Slider>
    </>
  )
}

export { TenantsView }
